import React, { useState, ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { Member, buildMember } from 'shared/types/ObjectTypes';
import MemberModal from './MemberModal';
import { inviteOrganizationMember } from 'workspaces/api/organizationsApi';
import { saveUserDetails } from 'workspaces/api/usersApi';
import { useTranslation } from 'react-i18next';

type ManageMemberModalProps = {
  onToggleModal: (saved?: boolean) => void;
  existingMember: Member;
  newMember: boolean;
  currentMember: Member;
  superuser: boolean;
  orgName?: string;
};

export function ManageMemberModal({
  onToggleModal,
  existingMember,
  newMember,
  currentMember,
  superuser,
  orgName,
}: ManageMemberModalProps) {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const { org_id } = useParams();
  const [member, setMember] = useState<Member>(buildMember());
  const [memberToUpdate, setMemberToUpdate] = useState<Member>(
    buildMember(
      existingMember.email,
      existingMember.first_name,
      existingMember.full_name,
      existingMember.last_name,
      existingMember.role,
      existingMember.id,
    ),
  );
  const [errors, setErrors] = useState({});

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const item = event.target;
    const item_id: string = item.id;
    newMember
      ? setMember((prevMember: Member) => ({
          ...prevMember,
          [item_id]: item.value,
        }))
      : setMemberToUpdate((prevMember: Member) => ({
          ...prevMember,
          [item_id]: item.value,
        }));
  }

  function handleSelectRole(e: any) {
    newMember
      ? setMember((prevMember: Member) => ({
          ...prevMember,
          role: e.value,
        }))
      : setMemberToUpdate((prevMember: Member) => ({
          ...prevMember,
          role: e.value,
        }));
  }

  const formIsValid = () => {
    let form_errors: any = {};
    const memberToCheck = newMember ? member : memberToUpdate;
    if (memberToCheck.first_name.length === 0)
      form_errors.first_name = t('First name is required');
    if (memberToCheck.last_name.length === 0)
      form_errors.last_name = t('Last name is required');
    if (memberToCheck.email.length === 0)
      form_errors.email = t('Email is required');
    if (!memberToCheck.role) form_errors.role = t('Role is required');

    setErrors(form_errors);
    return Object.keys(form_errors).length === 0;
  };

  async function handleInviteUser() {
    // Check if user is authenticated before making API calls
    const isLoggedIn =
      keycloak?.authenticated &&
      localStorage.getItem('userAuthorized') !== null;
    if (!isLoggedIn) {
      toast.error(t('Your session has expired. Please log in again.'), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    const memberdict = {
      email: member.email,
      role: member.role,
      first_name: member.first_name,
      last_name: member.last_name,
    };
    if (!formIsValid()) return;
    else {
      if (keycloak.token && org_id) {
        try {
          await inviteOrganizationMember(keycloak.token, org_id, memberdict);
          toast.success(`${t('Invitation sent')}.`, {
            position: toast.POSITION.TOP_RIGHT,
          });
          onToggleModal(true);
        } catch (err) {
          toast.error(t('Failed to send invitation. Please try again.'), {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      }
    }
  }

  async function handleSaveUser() {
    // Check if user is authenticated before making API calls
    const isLoggedIn =
      keycloak?.authenticated &&
      localStorage.getItem('userAuthorized') !== null;
    if (!isLoggedIn) {
      toast.error(t('Your session has expired. Please log in again.'), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!formIsValid()) return;
    else {
      if (keycloak.token && org_id && memberToUpdate.id) {
        try {
          await saveUserDetails(
            keycloak.token,
            org_id,
            memberToUpdate.id.toString(),
            memberToUpdate,
          );
          toast.success(`${t('Member successfully updated')}.`, {
            position: toast.POSITION.TOP_RIGHT,
          });
          onToggleModal(true);
        } catch (err) {
          // Silently handle errors - they're already handled by the interceptor
          console.log('Failed to save user:', err);
        }
      }
    }
  }

  const toggleModalVisibility: React.MouseEventHandler<
    HTMLButtonElement
  > = () => {
    onToggleModal();
  };

  // For new members: enabled once any field has a value.
  // For existing members: enabled only when a field differs from the original.
  const changesMade = newMember
    ? !!(
        member.email?.trim() ||
        member.role ||
        member.first_name?.trim() ||
        member.last_name?.trim()
      )
    : memberToUpdate.email !== existingMember.email ||
      memberToUpdate.first_name !== existingMember.first_name ||
      memberToUpdate.last_name !== existingMember.last_name ||
      memberToUpdate.role !== existingMember.role;

  return (
    <MemberModal
      member={member}
      existingMember={memberToUpdate}
      onChange={handleChange}
      onSelectRole={handleSelectRole}
      onToggleModal={toggleModalVisibility}
      onSave={newMember ? handleInviteUser : handleSaveUser}
      newMember={newMember}
      changesMade={changesMade}
      currentMember={currentMember}
      superuser={superuser}
      errors={errors}
      orgName={orgName}
    />
  );
}
